import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  EdgeTypes,
  EnvironmentGroupId,
  EnvironmentId,
} from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios';
import { useIntegratedLicenseInfo } from '@/react/portainer/licenses/use-license.service';
import { useEnvironmentList } from '@/react/portainer/environments/queries';
import {
  mutationOptions,
  withGlobalError,
  withInvalidate,
} from '@/react-tools/react-query';
import { queryKey as nodesCountQueryKey } from '@/react/portainer/system/useNodesCount';
import { LicenseType } from '@/react/portainer/licenses/types';
import { environmentQueryKeys } from '@/react/portainer/environments/queries/query-keys';
import { EdgeGroup } from '@/react/edge/edge-groups/types';
import { TagId } from 'Portainer/tags/types';

export function useAssociateDeviceMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    associateDevices,
    mutationOptions(
      withGlobalError('Failed to associate devices'),
      withInvalidate(queryClient, [
        environmentQueryKeys.base(),
        nodesCountQueryKey,
      ])
    )
  );
}

export interface EnvironmentRelationsPayload {
  edgeGroups: Array<EdgeGroup['Id']>;
  group: EnvironmentGroupId;
  tags: Array<TagId>;
}

async function associateDevices(
  environments: Record<EnvironmentId, EnvironmentRelationsPayload>
) {
  try {
    const payload = {
      EndpointIDs: Object.keys(environments).map(Number),
      Relations: environments,
    };
    await axios.post('/endpoints/edge/trust', payload);
  } catch (e) {
    throw parseAxiosError(e as Error, 'Failed to associate devices');
  }
}

export function useLicenseOverused(moreNodes: number) {
  const integratedInfo = useIntegratedLicenseInfo();

  return (
    !!integratedInfo &&
    integratedInfo.licenseInfo.type === LicenseType.Free &&
    integratedInfo.usedNodes + moreNodes > integratedInfo.licenseInfo.nodes
  );
}

export function useUntrustedCount() {
  const query = useEnvironmentList({
    edgeDeviceUntrusted: true,
    types: EdgeTypes,
  });
  return query.totalCount;
}
